import { DynamicPaging } from '@/components/utils/pagination';
import { Objects } from '@/utils/objects';
import { PaginationInput } from 'gql-types';
import { useCallback, useMemo, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type OffsetSnapshot<K extends string> = Record<K, number>;

export type ListSize = {
    count: number;
    total: number;
};

type Size = {
    page: number;
    setPage: (pageSize: number) => void;
    totalCount: number;
};

type Navigation = {
    currentPageIndex: number;
    canGoNext: boolean;
    canGoPrev: boolean;
    goNext: () => void;
    goPrev: () => void;
    setPage: (page: number) => void;
    resetPage: () => void;
};

export type UseUnifiedPagination<K extends string> = {
    inputs: Record<K, PaginationInput>;
    setListSize: Record<K, (listSize: ListSize) => void>;
    size: Size;
    navigation: Navigation;
};

const makeInitialSnapshot = <K extends string>(keys: K[]): OffsetSnapshot<K> =>
    Objects.fromEntries(keys.map((k) => [k, 0]));

/**
 * Manages independent offset cursors per entity type for a multi-entity paginated table.
 * Supports back-navigation via a cached offset stack (no refetch required).
 *
 * @param keys {string[]} - Treated as immutable after mount.
 * @param defaultPageSize {[number]}
 */
const useUnifiedPagination = <K extends string>(
    keys: K[],
    defaultPageSize: number = DEFAULT_PAGE_SIZE,
): UseUnifiedPagination<K> => {
    const keysRef = useRef<K[]>(keys);
    const [limit, setLimit] = useState(defaultPageSize);
    const [pageOffsets, setPageOffsets] = useState<OffsetSnapshot<K>[]>([
        makeInitialSnapshot(keysRef.current),
    ]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const currentPageIndexRef = useRef(currentPageIndex);
    currentPageIndexRef.current = currentPageIndex;
    const [totals, setTotals] = useState<Record<K, number>>(makeInitialSnapshot(keysRef.current));
    const hasSetListSize = useRef(false);

    const currentOffset = useMemo(
        () => pageOffsets[currentPageIndex] ?? makeInitialSnapshot(keysRef.current),
        [currentPageIndex, pageOffsets],
    );
    const allExhausted = useMemo(
        () => keysRef.current.every((k) => totals[k] > 0 && currentOffset[k] >= totals[k]),
        [currentOffset, totals],
    );

    const inputs = useMemo(
        (): Record<K, PaginationInput> =>
            Objects.fromEntries(
                keysRef.current.map((k) => [
                    k,
                    allExhausted ? { limit: 0, offset: 0 } : { limit, offset: currentOffset[k] },
                ]),
            ),
        [currentOffset, limit, allExhausted],
    );

    const setListSize = useMemo(
        (): Record<K, (listSize: ListSize) => void> =>
            Objects.fromEntries(
                keysRef.current.map((k) => [
                    k,
                    ({ count, total }: ListSize) => {
                        hasSetListSize.current = true;
                        const pageIndex = currentPageIndexRef.current;
                        setTotals((prev) => ({ ...prev, [k]: total }));
                        setPageOffsets((prev) => {
                            const currentOffset =
                                prev[pageIndex] ?? makeInitialSnapshot(keysRef.current);
                            const previousSnapshot =
                                prev[pageIndex + 1] ?? makeInitialSnapshot(keysRef.current);
                            const nextSnapshot = {
                                ...previousSnapshot,
                                [k]: currentOffset[k] + count,
                            };
                            return [...prev.slice(0, pageIndex + 1), nextSnapshot];
                        });
                    },
                ]),
            ),
        [],
    );

    const canGoPrev = currentPageIndex > 0;

    const canGoNext = useMemo(() => {
        if (!hasSetListSize.current) {
            return true;
        }
        if (pageOffsets[currentPageIndex + 1]) {
            return true;
        }
        return keysRef.current.some((k) => currentOffset[k] < totals[k]);
    }, [currentOffset, currentPageIndex, pageOffsets, totals]);

    const goNext = useCallback(() => {
        setCurrentPageIndex((prev) => prev + 1);
    }, []);

    const goPrev = useCallback(() => {
        setCurrentPageIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const setPage = useCallback((page: number) => {
        setCurrentPageIndex(page);
    }, []);

    const resetPage = useCallback(() => {
        setCurrentPageIndex(0);
        setPageOffsets([makeInitialSnapshot(keysRef.current)]);
        setTotals(makeInitialSnapshot(keysRef.current));
        hasSetListSize.current = false;
    }, []);

    const setSize = useCallback((newPageSize: number) => {
        setLimit(newPageSize);
        setCurrentPageIndex(0);
        setPageOffsets([makeInitialSnapshot(keysRef.current)]);
        hasSetListSize.current = false;
    }, []);

    const navigation = useMemo(
        () => ({
            currentPageIndex,
            canGoNext,
            canGoPrev,
            goNext,
            goPrev,
            resetPage,
            setPage,
        }),
        [canGoNext, setPage, canGoPrev, currentPageIndex, goNext, goPrev, resetPage],
    );

    const totalCount = useMemo(
        () => Objects.values(totals).reduce<number>((acc: number, total: number) => acc + total, 0),
        [totals],
    );

    const size = useMemo(
        () => ({
            page: limit,
            totalCount,
            setPage: setSize,
        }),
        [limit, totalCount, setSize],
    );

    return {
        inputs,
        setListSize,
        size,
        navigation,
    };
};

export type Pagination = DynamicPaging & {
    numRows: number;
};

export type UseUnifiedPaginationByPage<K extends string> = {
    inputs: Record<K, PaginationInput>;
    setListSize: Record<K, (listSize: ListSize) => void>;
    pagination: Pagination;
};

export const useUnifiedPaginationByPage = <K extends string>(
    keys: K[],
    defaultPageSize: number = DEFAULT_PAGE_SIZE,
): UseUnifiedPaginationByPage<K> => {
    const { navigation, size, setListSize, inputs } = useUnifiedPagination<K>(
        keys,
        defaultPageSize,
    );

    const pagination = useMemo(
        (): Pagination => ({
            page: navigation.currentPageIndex,
            setPage: navigation.setPage,
            pageSize: size.page,
            setPageSize: size.setPage,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            numRows: size.totalCount,
        }),
        [navigation.currentPageIndex, navigation.setPage, size.page, size.setPage, size.totalCount],
    );

    return useMemo(
        () => ({
            pagination,
            setListSize,
            inputs,
        }),
        [inputs, pagination, setListSize],
    );
};
